import { useState } from "react";
import api from '../config/api';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

import { useTranslation } from "react-i18next";
import { POINTS_SERVICES } from "./pointsServices";

/**
 * Diálogo para que el super-admin asigne puntos y módulos disponibles a una
 * organización. Si la organización no tiene el sistema de puntos activo,
 * el backend lo activa (plan escala) al guardar.
 * Puntos ilimitados (2026-10-29): switch que manda `unlimited` solo si
 * cambió. Al quitarlo, la organización queda con los «Puntos a cargar»
 * como saldo disponible (0 si se deja vacío); lo calcula el backend.
 */
const OrganizationPointsModal = ({
  organization,
  openModal,
  handleClose,
  setCounter,
  showAlert,
  authToken,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const pointsSystem = organization?.points_system;
  const isEnabled = Boolean(pointsSystem?.enabled);
  const currentLimit = pointsSystem?.points_limits ?? 0;
  const currentUsage = pointsSystem?.points_usage ?? 0;
  const unlimited = currentLimit === -1;
  const [unlimitedOn, setUnlimitedOn] = useState(unlimited);
  const removingUnlimited = unlimited && !unlimitedOn;

  const [pointsToLoad, setPointsToLoad] = useState("");
  const [selectedServices, setSelectedServices] = useState(
    isEnabled && Array.isArray(pointsSystem?.available_services) && pointsSystem.available_services.length > 0
      ? [...pointsSystem.available_services]
      : [...POINTS_SERVICES]
  );

  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { availableServices: selectedServices };
      if (unlimitedOn !== unlimited) payload.unlimited = unlimitedOn;
      // Con ilimitado no se cargan puntos
      const points = Number(pointsToLoad);
      if (!unlimitedOn && points > 0) payload.addPoints = points;

      const response = await api.put(
        `/organizations/${organization.uid}/points`,
        payload,
        { headers: { authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response.status_response === "OK") {
        showAlert(t("organizations.points.assignSuccess"), "info");
        if (setCounter) setCounter((prev) => prev + 1);
        handleClose();
      } else {
        showAlert(t("organizations.points.assignError"), "warning");
      }
    } catch (err) {
      console.error(err);
      showAlert(t("organizations.points.assignError"), "warning");
    }
    setLoading(false);
  };

  return (
    <Modal disableScrollLock={true} open={openModal} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 540,
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          paddingBottom: 2,
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" component="h5" sx={{ textAlign: "center" }}>
                {t("organizations.points.manageTitle")}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", color: "text.secondary" }}
              >
                {organization?.name}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {isEnabled ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={`${t("organizations.points.currentUsage")}: ${Number(currentUsage).toFixed(2)}`}
                    size="small"
                  />
                  <Chip
                    label={`${t("organizations.points.currentLimit")}: ${
                      unlimited ? t("organizations.points.unlimited") : currentLimit
                    }`}
                    size="small"
                  />
                  {!unlimited && (
                    <Chip
                      color={currentLimit - currentUsage > 0 ? "success" : "error"}
                      label={`${t("organizations.points.available")}: ${
                        (Math.round((currentLimit - currentUsage) * 100) / 100).toFixed(2)
                      }`}
                      size="small"
                    />
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  {t("organizations.points.willActivate")}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={unlimitedOn}
                    onChange={(e) => setUnlimitedOn(e.target.checked)}
                  />
                }
                label={t("organizations.points.unlimitedSwitch")}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t("organizations.points.unlimitedSwitchHelper")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("organizations.points.pointsToLoad")}
                type="number"
                value={pointsToLoad}
                onChange={(e) => setPointsToLoad(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                size="small"
                disabled={unlimitedOn}
                helperText={
                  unlimitedOn
                    ? t("organizations.points.unlimitedHelper")
                    : removingUnlimited
                      ? t("organizations.points.unlimitedOffHelper")
                      : ""
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">
                {t("organizations.points.modulesTitle")}
              </Typography>
              <FormGroup sx={{ maxHeight: 200, overflowY: "auto" }}>
                {POINTS_SERVICES.map((service) => (
                  <FormControlLabel
                    key={service}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedServices.includes(service)}
                        onChange={() => toggleService(service)}
                      />
                    }
                    label={t(`organizations.points.services.${service}`)}
                  />
                ))}
              </FormGroup>
            </Grid>

            {unlimitedOn && selectedServices.includes("hunting") && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {t("organizations.points.huntingUnlimitedWarning")}
                </Alert>
              </Grid>
            )}

            <Grid item xs={6} sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <LoadingButton
                variant="contained"
                loading={loading}
                onClick={handleSave}
                loadingIndicator={t("saving")}
                sx={{ boxShadow: "none", width: "90%" }}
              >
                {t("save")}
              </LoadingButton>
            </Grid>
            <Grid item xs={6} sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                color="inherit"
                variant="outlined"
                sx={{ width: "90%" }}
                onClick={handleClose}
              >
                {t("cancel")}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Modal>
  );
};

export default OrganizationPointsModal;
